import React from "react";

import {Menu, MenuButton, MenuItem, MenuItems} from "@headlessui/react";
import {
    DragDropContext,
    Draggable,
    DraggableProvided,
    DropResult,
    Droppable,
    DroppableProvided,
} from "@hello-pangea/dnd";
import {EllipsisVerticalIcon} from "@heroicons/react/20/solid";
import * as Sentry from "@sentry/browser";
import {Decimal} from "decimal.js";
import {AnimatePresence, PanInfo, motion} from "framer-motion";
import {produce} from "immer";
import {createPortal} from "react-dom";
import {RequesterResult} from "reactivated/dist/rpc";
import {P, match} from "ts-pattern";
import * as tus from "tus-js-client";

import * as dialogs from "@client/features/dialogs";
import {notify} from "@client/features/notifications";
import * as workouts from "@client/features/workouts";

import * as constants from "@client/constants";
import * as analytics from "@client/shared/analytics";
import * as typography from "@client/shared/typography";
import * as utils from "@client/utils";

import {
    ExerciseSetFields,
    ExerciseSetType,
    ExerciseSetsPreview,
    getSetPosition,
} from "@client/components/ExerciseSet";
import {ExerciseSetOptions} from "@client/components/ExerciseSetOptions";
import {Icon} from "@client/components/Icon";
import {ContentModal} from "@client/components/Modal";
import {Prose} from "@client/components/Prose";
import {Cardio} from "@client/components/cardio/Cardio";
import {makeSteadySet, seedIntervalSets} from "@client/components/cardio/model";
import * as widgets from "@client/forms/widgets";
import {ActionsDefinition} from "@client/gotterdammerung";
import * as hardware from "@client/hardware";
import {useAsync} from "@client/hooks";
import * as journeys from "@client/journeys";
import {useSiteContext} from "@client/site/context";
import * as forms from "@client/site/forms";
import {store, useAppDispatch} from "@client/store";
import {classNames, models, reverse, server} from "@reactivated";

import {SectionDialog} from "./SectionDialog";

export const ProgramUser = React.createContext<{user_id: number} | null>(null);

export const WorkoutSectionContext = React.createContext<workouts.WorkoutSection>(
    null!,
);

export async function uploadVideo({
    file,
    onProgress,
    user_id,
}: {
    file: File;
    onProgress: (percentage: string) => void;
    user_id: number;
}) {
    let video_uid: string | null = null;

    return new Promise<string>((resolve, reject) => {
        const upload = new tus.Upload(file, {
            endpoint:
                utils.getBaseURLWithOriginForCapacitor() +
                reverse("get_tus_upload_url"),
            chunkSize: 150 * 1024 * 1024,
            retryDelays: [0, 3000, 5000, 10000, 20000],
            metadata: {
                name: file.name,
                user_id: user_id.toString(),
                filetype: file.type,
            },
            onError: function (error) {
                // console.log("Failed because: " + error);
                reject(error);
            },
            onProgress: function (bytesUploaded, bytesTotal) {
                const percentage = (bytesUploaded / bytesTotal) * 100;
                // console.log(bytesUploaded, bytesTotal, percentage.toFixed(2) + "%");
                onProgress(percentage.toFixed(0));
            },
            onSuccess: function () {
                // console.log("Download %s from %s", file.name, upload.url);
                resolve(video_uid!);
                void analytics.track("UPLOADED_VIDEO", {
                    video_size: file.size,
                    video_type: file.type,
                });
            },
            onAfterResponse: function (req, res) {
                video_uid = res.getHeader("stream-media-id");
            },
        });
        upload.start();
    });
}

export function getPercentageExercises(
    exercises: server.journal.workouts.Exercise.output[],
) {
    return [
        ...new Set(
            exercises
                .map((exercise) => exercise.sets.map((set) => ({exercise, set})))
                .flat()
                .filter((setAndType) => setAndType.set.weight_type == "PERCENTAGE")
                .map((set) => set.exercise.type),
        ),
    ];
}

export function InstructionsModal(props: {
    name: string;
    isOpen: boolean;
    onClose: () => void;
    instructions: string;
    video_url: string | null;
}) {
    // Some sort of portal empty div is added on the modal location,
    // despite it showing outside the dom tree.
    // This creates issues with space-x and space-y and gap utilities
    // So we add a hidden class.
    return (
        <div hidden>
            <ContentModal isOpen={props.isOpen} setOpen={props.onClose}>
                <div>
                    <div className="max-h-[70vh] overflow-auto pb-4">
                        <div className="flex items-center gap-4 px-6 py-4">
                            <h1 className="flex-1 text-2xl">{props.name}</h1>
                            {props.video_url != null && props.video_url != "" && (
                                <a
                                    className="
                                        rounded-sm bg-indigo-600 px-1.5 py-1 text-xs
                                        font-semibold text-white shadow-xs
                                        hover:bg-indigo-500
                                        focus-visible:outline-2
                                        focus-visible:outline-offset-2
                                        focus-visible:outline-indigo-600
                                        focus-visible:outline-solid
                                        disabled:border-slate-200 disabled:bg-slate-50
                                        disabled:text-slate-500 disabled:shadow-none
                                    "
                                    target="_blank"
                                    href={props.video_url}
                                    rel="noreferrer"
                                >
                                    <Icon icon="0875-play-circle">Video</Icon>
                                </a>
                            )}
                        </div>
                        <div className="px-6">
                            <Prose text={props.instructions} />
                        </div>
                    </div>
                    <div className="p-6">
                        <button
                            type="submit"
                            className="
                                w-full rounded-sm bg-indigo-600 px-2.5 py-1.5 text-sm
                                font-semibold text-white shadow-xs
                                hover:bg-indigo-500
                                focus-visible:outline-2 focus-visible:outline-offset-2
                                focus-visible:outline-indigo-600
                                focus-visible:outline-solid
                                disabled:border-slate-200 disabled:bg-slate-50
                                disabled:text-slate-500 disabled:shadow-none
                            "
                            onClick={() => props.onClose()}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </ContentModal>
        </div>
    );
}

export async function processResponse<T extends RequesterResult>(
    result: Promise<T>,
    // Optimistic updates already repaint the UI locally and fire the request
    // fire-and-forget, so they pass `quiet` to skip the global loader while
    // still reporting failures to Sentry.
    options: {quiet?: boolean} = {},
) {
    const dispatch: ReturnType<typeof useAppDispatch> = store.dispatch;

    if (options.quiet != true) {
        dispatch({type: "ACTION_OUTSTANDING"});
    }

    const awaited = await result;

    if (options.quiet != true) {
        dispatch({type: "ACTION_COMPLETED"});
    }

    if (awaited.type != "success") {
        Sentry.withScope((scope) => {
            scope.setTag("feature", "workouts");
            Sentry.captureException(awaited);
        });
    }

    return result;
}

interface OptionsProps {
    readOnly?: boolean;
    alignRight?: boolean;
    items: Array<
        | {
              title: string;
              icon: constants.Icon;
              onClick?: () => void;
              selected?: boolean;
          }
        | null
        | false
    >;
}

export function Options(props: OptionsProps) {
    return (
        <Menu>
            <MenuButton
                disabled={props.readOnly}
                className="
                    flex items-center rounded-full bg-stone-200/70 p-1.5 text-gray-400
                    hover:text-gray-600
                    focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                    focus:ring-offset-gray-100 focus:outline-hidden
                    disabled:opacity-50
                "
            >
                <span className="sr-only">Open options</span>
                <EllipsisVerticalIcon
                    className="size-5 text-gray-600"
                    aria-hidden="true"
                />
            </MenuButton>
            <MenuItems
                anchor="bottom start"
                className="
                    absolute z-10 origin-top-right divide-y divide-gray-100 rounded-md
                    bg-white shadow-lg ring-1 ring-black/5 [--anchor-gap:4px]
                    focus:outline-hidden
                "
            >
                {props.items
                    .filter(
                        (item): item is Exclude<NonNullable<typeof item>, false> =>
                            item != false && item != null,
                    )
                    .map((item, index) => (
                        <MenuItem key={index}>
                            {({active}) => (
                                <button
                                    onClick={item.onClick}
                                    className={classNames(
                                        active
                                            ? "bg-gray-100 text-gray-900"
                                            : "text-gray-700",
                                        "w-full whitespace-nowrap block px-4 py-2 text-sm",
                                        item.selected == true && "font-semibold",
                                    )}
                                >
                                    <Icon icon={item.icon}>{item.title}</Icon>
                                </button>
                            )}
                        </MenuItem>
                    ))}
            </MenuItems>
        </Menu>
    );
    return (
        <Menu as="div" className="relative inline-block text-left">
            <div>
                <Menu.Button
                    disabled={props.readOnly}
                    className="
                        flex items-center rounded-full bg-gray-100 p-1.5 text-gray-400
                        hover:text-gray-600
                        focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                        focus:ring-offset-gray-100 focus:outline-hidden
                        disabled:opacity-50
                    "
                >
                    <span className="sr-only">Open options</span>
                    <EllipsisVerticalIcon
                        className="size-5 text-gray-600"
                        aria-hidden="true"
                    />
                </Menu.Button>
            </div>
        </Menu>
    );
}

export function ExerciseDetailModal(props: {
    exercise: workouts.Exercise;
    workout_uuid: UUID;
    isOpen: boolean;
    onClose: () => void;
    onSetExerciseSets: (
        exerciseHandler: workouts.Exercise,
        nextExercise: server.journal.workouts.ExerciseHistory.output,
    ) => void;
}) {
    const programUser = React.useContext(ProgramUser);

    const data = useAsync(() =>
        server.journal.workouts.exercise_detail({
            exercise_type_uuid: props.exercise.type.uuid,
            workout_uuid: props.workout_uuid,
            user_id: programUser?.user_id ?? null,
        }),
    );

    // Some sort of portal empty div is added on the modal location,
    // despite it showing outside the dom tree.
    // This creates issues with space-x and space-y and gap utilities
    // So we add a hidden class.
    return (
        <div hidden>
            <ContentModal isOpen={props.isOpen} setOpen={props.onClose}>
                <div>
                    <div className="h-[70vh] overflow-auto pb-4">
                        <div className="flex items-center gap-4 px-6 pt-4 pb-2">
                            <h1 className="flex-1 text-2xl">
                                {props.exercise.type.name}
                            </h1>
                        </div>
                        <div className="flex flex-col gap-6 px-6">
                            {data.value == null || data.value.type != "success" ? (
                                <div>Loading...</div>
                            ) : (
                                <>
                                    {data.value.data.map((exercise) => (
                                        <div key={exercise.uuid}>
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1">
                                                    {exercise.group.section.workout
                                                        .date == null
                                                        ? exercise.type.name
                                                        : utils.formatDate(
                                                              exercise.group.section
                                                                  .workout.date,
                                                          )}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        props.onClose();
                                                        props.onSetExerciseSets(
                                                            props.exercise,
                                                            exercise,
                                                        );
                                                    }}
                                                    className="
                                                        rounded-sm bg-white px-2 py-1
                                                        text-xs font-semibold
                                                        text-gray-900 shadow-xs ring-1
                                                        ring-gray-300 ring-inset
                                                        hover:bg-gray-50
                                                    "
                                                >
                                                    Select
                                                </button>
                                            </div>

                                            <div className="mt-2 space-y-2">
                                                <ExerciseSetsPreview
                                                    exercise={exercise}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    </div>
                    <div className="p-6">
                        <button
                            type="submit"
                            className="
                                w-full rounded-sm bg-indigo-600 px-2.5 py-1.5 text-sm
                                font-semibold text-white shadow-xs
                                hover:bg-indigo-500
                                focus-visible:outline-2 focus-visible:outline-offset-2
                                focus-visible:outline-indigo-600
                                focus-visible:outline-solid
                                disabled:border-slate-200 disabled:bg-slate-50
                                disabled:text-slate-500 disabled:shadow-none
                            "
                            onClick={() => props.onClose()}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </ContentModal>
        </div>
    );
}

interface ExerciseSetProps {
    readOnly: boolean;
    exerciseSetOrGroup:
        | {type: "group"; group: workouts.ExerciseSet[]}
        | {type: "set"; set: workouts.ExerciseSet};
    // The row is only ever rendered for the strength set list, so its exercise is
    // category-narrowed — cardio never reaches here.
    exercise: workouts.StrengthExercise;
    deleteExerciseSet: (exercise: workouts.Exercise, exerciseSetUUID: string) => void;
    draggable: DraggableProvided;
    isTemplate: boolean;
    isDragging: boolean;
    position: number;
}

const ExerciseSet = ({
    isDragging,
    readOnly,
    isTemplate,
    exerciseSetOrGroup,
    exercise,
    deleteExerciseSet,
    draggable,
    position,
}: ExerciseSetProps) => {
    const deleteButtonRef = React.useRef<HTMLButtonElement>(null);
    const {realm} = useSiteContext();
    const dispatch = useAppDispatch();

    const {exerciseSet, count} =
        exerciseSetOrGroup.type == "group"
            ? {
                  exerciseSet: exerciseSetOrGroup.group[0],
                  count: exerciseSetOrGroup.group.length,
              }
            : {exerciseSet: exerciseSetOrGroup.set, count: null};

    const commonAttributes: Array<{
        title: string;
        icon: constants.Icon;
        attribute: models["core.models.ExerciseSet.Type"];
    }> = [
        {
            title: "Warm-up",
            icon: "0057-fire",
            attribute: "WARM_UP",
        },
        {
            title: "Working set",
            icon: "0120-hammer",
            attribute: "WORKING",
        },
        {
            title: "Max",
            icon: "0222-medal-empty",
            attribute: "MAX",
        },
    ];

    const attributes = commonAttributes.concat(
        exercise.type.category == "WEIGHT" || exercise.type.category == "BODYWEIGHT"
            ? [
                  {
                      title: "AMRAP",
                      icon: "0516-rank3",
                      attribute: "AMRAP",
                  },
                  {
                      title: "Failure",
                      icon: "0654-sad",
                      attribute: "FAILURE",
                  },
              ]
            : exercise.type.category == "DURATION"
              ? [
                    {
                        title: "ALAP",
                        icon: "0516-rank3",
                        attribute: "AMRAP",
                    },
                ]
              : [],
    );

    const [didDeleteHaptic, setDidDeleteHaptic] = React.useState(false);

    const swipedEnoughToDelete = (info: PanInfo) => {
        if (deleteButtonRef.current == null || info.point.x == 0) {
            return false;
        }

        if (Math.abs(info.offset.x / 3) > deleteButtonRef.current.offsetWidth) {
            return true;
        }
        return false;
    };

    const handleDeleteSwiping = (event: unknown, info: PanInfo) => {
        if (swipedEnoughToDelete(info) && didDeleteHaptic == false) {
            setDidDeleteHaptic(true);

            void hardware.Haptics.impact({
                style: hardware.ImpactStyle.Heavy,
            });
        }
    };

    const handleDeleteSwiped = (event: unknown, info: PanInfo) => {
        setDidDeleteHaptic(false);

        if (swipedEnoughToDelete(info)) {
            void deleteExerciseSet(exercise, exerciseSet.uuid);
        }
    };

    const canSwipe = isDragging == false && readOnly == false;

    // Swipe and animation from https://codesandbox.io/p/sandbox/framer-motion-ios-messages-swipe-to-delete-elmjc
    return (
        <motion.div
            ref={draggable.innerRef}
            {...draggable.draggableProps}
            role="row"
            className={classNames(
                "relative",
                // we don't use space-y-1 or space-y-2 because drag/drop needs
                // consistent padding in between otherwise it's janky.  But
                // multi line sets need more padding between them.  We offset
                // the exercise container having a smaller gap between title
                // and add set. No clean way to do this.
                exercise.type.category == "WEIGHT_DURATION_DISTANCE" ? "py-2" : "py-1",
                isDragging && "bg-indigo-50! rounded-md",
            )}
            exit={{height: 3, opacity: 0}}
            transition={{opacity: {transition: {duration: 0}}}}
        >
            <motion.div
                drag={canSwipe ? "x" : false}
                dragConstraints={{left: 0, right: 0}}
                onDrag={handleDeleteSwiping}
                onDragEnd={handleDeleteSwiped}
                onFocus={forms.handleInputFocusSelectAndPreventContextMenu}
                className={classNames(
                    "bg-stone-50",
                    "z-6 relative flex gap-1.5 sm:gap-2",
                    exercise.type.category == "WEIGHT_DURATION_DISTANCE"
                        ? "items-center"
                        : "items-center",
                    isDragging && "bg-indigo-50!",
                )}
            >
                <div className="flex items-center gap-1" {...draggable.dragHandleProps}>
                    <Icon icon="arrows" />
                    <ExerciseSetType
                        count={count}
                        type={exerciseSet.type}
                        position={position}
                    />
                </div>
                <workouts.ExerciseContext.Provider value={{exercise}}>
                    <ExerciseSetFields
                        handler={exerciseSet}
                        readOnly={readOnly}
                        isTemplate={isTemplate}
                        category={exercise.type.category}
                    />
                </workouts.ExerciseContext.Provider>
                <ExerciseSetOptions
                    exercise={exercise}
                    exerciseSet={exerciseSet}
                    showAttributes={exercise.type.name
                        .toLowerCase()
                        .includes("deadlift")}
                    items={attributes
                        // .filter((attribute) => form.values.type != attribute.attribute)
                        .map((attribute) => {
                            const selected = exerciseSet.type == attribute.attribute;
                            return {
                                title: attribute.title,
                                icon: attribute.icon,
                                selected,
                                onClick: () => {
                                    if (selected == true) {
                                        exerciseSet.__update({type: "WORKING"});
                                    } else {
                                        exerciseSet.__update({
                                            type: attribute.attribute,
                                        });
                                    }
                                },
                            };
                        })
                        .concat(
                            exerciseSet.status != "SKIPPED"
                                ? [
                                      {
                                          title: "Skip",
                                          icon: "0879-forward-circle",
                                          selected: false,
                                          onClick: () => {
                                              exerciseSet.__update({status: "SKIPPED"});
                                          },
                                      },
                                  ]
                                : [],
                        )
                        .concat([
                            {
                                title: "Delete",
                                icon: "remove",
                                selected: false,
                                onClick: () => {
                                    void deleteExerciseSet(exercise, exerciseSet.uuid);

                                    if (realm == "app") {
                                        dispatch(
                                            notify(
                                                "**Tip**: swipe left on an exercise set to delete it.",
                                                {type: "info", permanent: true},
                                            ),
                                        );
                                    }
                                },
                            },
                        ])}
                />
            </motion.div>
            <div className="absolute top-0 right-0 flex h-full p-[inherit]">
                <button
                    ref={deleteButtonRef}
                    className="
                        bg-rose-600 px-4 text-xs font-semibold text-white lowercase
                    "
                >
                    Delete
                </button>
            </div>
        </motion.div>
    );
};

// Strength-style categories: a sortable list of sets, each wrapped in the
// ExerciseSet row chrome (drag handle, number badge, options, completion).
const StrengthSets = (props: {
    exercise: workouts.StrengthExercise;
    readOnly: boolean;
    isTemplate: boolean;
    onDragStart: () => void;
    onDragEnd: (result: DropResult) => void;
    deleteExerciseSet: (exercise: workouts.Exercise, exerciseSetUUID: string) => void;
}) => (
    <DragDropContext onDragStart={props.onDragStart} onDragEnd={props.onDragEnd}>
        <Droppable droppableId={props.exercise.uuid} type="EXERCISE_SET">
            {(provided) => (
                <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="flex flex-col"
                >
                    <AnimatePresence>
                        {props.exercise.sets.map((exerciseSet, exerciseSetIndex) => (
                            <Draggable
                                isDragDisabled={props.readOnly}
                                key={exerciseSet.uuid}
                                draggableId={exerciseSet.uuid}
                                index={exerciseSetIndex}
                            >
                                {(draggableProvided, snapshot) => (
                                    <ExerciseSet
                                        isDragging={snapshot.isDragging}
                                        position={getSetPosition(
                                            props.exercise.sets,
                                            exerciseSetIndex,
                                        )}
                                        readOnly={props.readOnly}
                                        draggable={draggableProvided}
                                        exerciseSetOrGroup={{
                                            type: "set",
                                            set: exerciseSet,
                                        }}
                                        isTemplate={props.isTemplate}
                                        exercise={props.exercise}
                                        deleteExerciseSet={props.deleteExerciseSet}
                                    />
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </AnimatePresence>
                </div>
            )}
        </Droppable>
    </DragDropContext>
);

const Exercise = (props: {
    newHandlers: WorkoutHandlers;
    section: workouts.WorkoutSection;
    previousGroup: workouts.ExerciseGroup | null;
    exerciseGroup: workouts.ExerciseGroup;
    exercise: workouts.Exercise;
    workout_uuid: UUID;

    isFirst: boolean;
    groupDraggable: DraggableProvided | null;
    isDragging: boolean;
    readOnly: boolean;
    isTemplate: boolean;
    handlers: {
        addSet: (exercise: workouts.Exercise) => void;
        addToSuperSet: (
            previousGroup: workouts.ExerciseGroup | null,
            currentGroup: workouts.ExerciseGroup,
            exercise: workouts.Exercise,
        ) => void;
        removeFromSuperSet: (
            group: workouts.ExerciseGroup,
            exercise: workouts.Exercise,
        ) => void;
        deleteExerciseSet: (
            exercise: workouts.Exercise,
            exerciseSetUUID: string,
        ) => void;
        recentlyAddedExercise:
            | `${string}-${string}-${string}-${string}-${string}`
            | null;
        setExerciseSets: (
            exerciseHandler: workouts.Exercise,
            nextExercise: server.journal.workouts.ExerciseHistory.output,
        ) => void;
        clearExerciseSets: (exercise: workouts.Exercise) => void;
        onDragStart: () => void;
        onDragEnd: (result: DropResult) => void;
        isTemplate: boolean;
    };
}) => {
    const isImperial = forms.useIsImperial();
    const workoutData = workouts.useWorkoutData();
    const workoutContext = React.useContext(workouts.WorkoutContext);
    const {profile, realm} = useSiteContext();
    const {exercise, exerciseGroup} = props;

    // Narrow the exercise by category once. The discriminant lives under
    // `exercise.type.category` (nested), so plain control flow can't carry the
    // narrowing forward — ts-pattern can. Everything downstream switches on
    // `variant.kind` and uses the already-narrowed `variant.exercise`, so there
    // are no scattered category checks or casts.
    const variant = match(exercise)
        .with({type: {category: "CARDIO"}}, (cardio) => ({
            kind: "cardio" as const,
            exercise: cardio,
        }))
        .with(
            {
                type: {
                    category: P.union(
                        "WEIGHT",
                        "BODYWEIGHT",
                        "DURATION",
                        "REPS_DISTANCE",
                        "MEDLEY",
                        "WEIGHT_DISTANCE",
                        "WEIGHT_DURATION_DISTANCE",
                        "DURATION_DISTANCE",
                    ),
                },
            },
            (strength) => ({kind: "strength" as const, exercise: strength}),
        )
        .exhaustive();
    const [moreExerciseType, setMoreExerciseType] =
        React.useState<server.journal.workouts.ExerciseType.output | null>(null);

    const [showExerciseDetail, setShowExerciseDetail] = React.useState(false);

    const [showInstructions, setShowInstructions] = React.useState(
        props.isTemplate == true && exercise.instructions != "",
    );
    const [showProgression, setShowProgression] = React.useState<UUID | "" | null>(
        props.isTemplate == true ? (exercise.progression?.uuid ?? null) : null,
    );

    const videoDialog = dialogs.useDialog<unknown, {video_uid: string}>({
        Content: ({video_uid}) => (
            <div className="relative aspect-video w-[95vw] max-w-[1024px]">
                <div className="absolute inset-0 z-1 flex items-center justify-center">
                    <motion.div
                        className="
                            size-6 rounded-full border-2 border-gray-300
                            border-t-indigo-700 will-change-transform
                        "
                        animate={{rotate: 360}}
                        transition={{
                            duration: 1,
                            repeat: Infinity,
                            ease: "linear",
                        }}
                    />
                </div>
                <iframe
                    src={`https://customer-zze5w8c396zl9f2i.cloudflarestream.com/${video_uid}/iframe`}
                    style={{border: "none"}}
                    className="relative z-10 size-full"
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                    allowFullScreen
                />
            </div>
        ),
    });
    const {
        addSet,
        deleteExerciseSet,
        recentlyAddedExercise,
        clearExerciseSets,
        onDragStart,
        onDragEnd,
    } = props.handlers;
    const dispatch = useAppDispatch();

    const definitionForm = forms.usePform(
        {
            instructions: {
                type: "textarea",
                placeholder: "Instructions for these sets...",
            },
        },
        exercise,
        (values) => {
            exercise.__update({
                instructions: values.instructions,
            });
        },
    );

    const progressionForm = forms.usePform(
        {
            progression: {
                type: "select",
                widget: "select",
                required: false,
                options: [
                    ["", workoutData == null ? "Loading ..." : "<New progression>"] as [
                        "" | UUID,
                        string,
                    ],
                ].concat(
                    (workoutData?.exercise_progressions ?? []).map(
                        (p) => [p.uuid, p.name] as ["" | UUID, string],
                    ),
                ),
            },
        },
        {progression: showProgression},
        (values) => {
            if (exercise.progression == null) {
                return;
            }
            setShowProgression(values.progression);

            if (values.progression == "") {
                exercise.__update({
                    progression: {
                        ...exercise.progression,
                        uuid: crypto.randomUUID(),
                        name: `${exercise.type.name} progression`,
                    },
                });
            } else {
                const progression = workoutData?.exercise_progressions.find(
                    (p) => p.uuid == values.progression,
                );

                if (progression != null) {
                    exercise.__update({progression});
                }
            }
        },
    );

    const progressionDetailsForm = forms.usePform(
        {
            name: {type: "text"},
            weight: {type: "decimal", required: false, label: "Weight increase"},
            type: {
                type: "select",
                required: true,
                options: [
                    ["LINEAR", "Linear"],
                    ["DOUBLE_LINEAR", "Double"],
                ],
            },
        },
        {
            name: exercise.progression?.name ?? "",
            weight: exercise.progression?.weight ?? null,
            type: exercise.progression?.type ?? "LINEAR",
        },
        (values) => {
            if (exercise.progression != null) {
                exercise.__update({
                    progression: {
                        uuid: exercise.progression.uuid,
                        ...values,
                    },
                });
            }
        },
    );

    const programUser = React.useContext(ProgramUser);
    const [uploadingVideo, setUploadingVideo] = React.useState<
        null | "uploading" | "done"
    >(null);

    const hostedVideoAvailable = exercise.type.video_uid;
    const defaultVideoAvailable = exercise.type.media?.video_uid;
    const fileInputRef = React.useRef<HTMLInputElement | null>(null);

    const {video_uid: clientVideoUID} = exercise;

    const allSetsSkipped =
        exercise.sets.length > 0 &&
        exercise.sets.every((set) => set.status == "SKIPPED");

    const skipAllSets = () => {
        exercise.sets.forEach((set) => {
            set.__update({status: "SKIPPED"});
        });
    };

    const unskipAllSets = () => {
        void hardware.haptic("Light");
        exercise.sets.forEach((set) => {
            set.__update({status: "INCOMPLETE"});
        });
    };

    return (
        <div
            key={exercise.uuid}
            data-testid={`exercise-${exercise.uuid}`}
            className={
                // Compensate for inner spacing being smaller on multiline sets.
                exercise.type.category == "WEIGHT_DURATION_DISTANCE" ||
                exercise.type.category == "CARDIO"
                    ? "space-y-0.5"
                    : "space-y-1"
            }
        >
            {showExerciseDetail && (
                <ExerciseDetailModal
                    onClose={() => setShowExerciseDetail(false)}
                    onSetExerciseSets={props.handlers.setExerciseSets}
                    isOpen={showExerciseDetail}
                    exercise={exercise}
                    workout_uuid={props.workout_uuid}
                />
            )}
            <InstructionsModal
                onClose={() => setMoreExerciseType(null)}
                instructions={moreExerciseType?.description ?? ""}
                isOpen={moreExerciseType != null}
                video_url={moreExerciseType?.video_url ?? ""}
                name={moreExerciseType?.name ?? ""}
            />
            <div className="flex items-center gap-2">
                {videoDialog.dialog}
                {props.groupDraggable != null ? (
                    <div
                        className="flex items-center gap-1 truncate"
                        {...props.groupDraggable.dragHandleProps}
                    >
                        <Icon icon="arrows" />
                        <h2
                            className="
                                truncate text-base
                                sm:text-lg
                            "
                        >
                            {exercise.type.name}
                        </h2>
                    </div>
                ) : (
                    <div className="flex items-center gap-1 truncate">
                        <h2
                            className="
                                truncate text-base
                                sm:text-lg
                            "
                        >
                            {exercise.type.name}
                        </h2>
                    </div>
                )}

                {variant.kind === "cardio" && (
                    <div
                        className="
                            ml-auto flex h-[25px] shrink-0 overflow-hidden rounded-sm
                            text-xs font-semibold ring-1 ring-indigo-600 ring-inset
                        "
                    >
                        {(
                            [
                                ["STRAIGHT", "Steady"],
                                ["INTERVALS", "Intervals"],
                            ] as const
                        ).map(([structure, label]) => (
                            <button
                                key={structure}
                                type="button"
                                disabled={props.readOnly}
                                onClick={() => {
                                    if (exercise.structure == structure) {
                                        return;
                                    }

                                    hardware.haptic("Medium");

                                    exercise.__update(
                                        structure == "INTERVALS"
                                            ? {
                                                  structure,
                                                  rest_duration: null,
                                                  sets: seedIntervalSets(
                                                      isImperial,
                                                      props.isTemplate,
                                                  ),
                                              }
                                            : {
                                                  structure,
                                                  sets: [
                                                      makeSteadySet(
                                                          isImperial,
                                                          props.isTemplate,
                                                      ),
                                                  ],
                                              },
                                    );

                                    if (props.isTemplate == false) {
                                        void processResponse(
                                            server.journal.workouts.clear_exercise_sets(
                                                {
                                                    exercise_uuid: exercise.uuid,
                                                },
                                            ),
                                            {quiet: true},
                                        );
                                    }
                                }}
                                className={classNames(
                                    "px-2 py-1",
                                    exercise.structure == structure
                                        ? "bg-indigo-600 text-white"
                                        : "bg-white text-indigo-600",
                                )}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                )}

                <Options
                    readOnly={props.readOnly}
                    alignRight={exercise.type.name.length < 15}
                    items={[
                        props.isTemplate == false
                            ? null
                            : showInstructions
                              ? {
                                    title: "Remove instructions",
                                    icon: "0018-eraser",
                                    onClick: () => {
                                        exercise.__update({instructions: ""});
                                        setShowInstructions(false);
                                    },
                                }
                              : {
                                    title: "Add instructions",
                                    icon: "0020-pencil5",
                                    onClick: () => setShowInstructions(true),
                                },
                        props.isTemplate == false ||
                        profile?.user.has_beta_access != true
                            ? null
                            : showProgression != null
                              ? {
                                    title: "Remove progression",
                                    icon: "0018-eraser",
                                    onClick: () => {
                                        exercise.__update({progression: null});
                                        setShowProgression(null);
                                    },
                                }
                              : {
                                    title: "Add progression",
                                    icon: "0508-chart-growth",
                                    onClick: () => {
                                        const uuid = crypto.randomUUID();
                                        exercise.__update({
                                            progression: {
                                                uuid,
                                                name: `${exercise.type.name} Progression`,
                                                weight: new Decimal(
                                                    isImperial ? "5" : "2.5",
                                                )
                                                    .times(
                                                        isImperial ? "0.45359237" : "1",
                                                    )
                                                    .toString(),
                                                type: "LINEAR",
                                            },
                                        });
                                        setShowProgression("");
                                    },
                                },
                        props.isTemplate == false || programUser != null
                            ? {
                                  title: "History",
                                  icon: "0394-calendar-full",
                                  onClick: () => setShowExerciseDetail(true),
                              }
                            : null,
                        defaultVideoAvailable != null
                            ? {
                                  title: "Video",
                                  icon: "0875-play-circle",
                                  onClick: () => {
                                      void analytics.track("EXERCISE_VIDEO_PLAYED");
                                      void videoDialog({
                                          video_uid: defaultVideoAvailable,
                                      });
                                  },
                              }
                            : null,
                        exercise.type.description != ""
                            ? {
                                  title: "Help",
                                  icon: "0856-question-circle",
                                  onClick: () => {
                                      void analytics.track(
                                          "CLICKED_EXERCISE_TYPE_HELP",
                                      );
                                      setMoreExerciseType(exercise.type);
                                  },
                              }
                            : null,
                        exerciseGroup.exercises.length > 1
                            ? {
                                  title: "Singleset",
                                  icon: "0845-expand2",
                                  onClick: () => {
                                      props.handlers.removeFromSuperSet(
                                          exerciseGroup,
                                          exercise,
                                      );
                                  },
                              }
                            : null,
                        exerciseGroup.exercises.length == 1 && props.isFirst == false
                            ? {
                                  title: "Superset",
                                  icon: "compress",
                                  onClick: () => {
                                      props.handlers.addToSuperSet(
                                          props.previousGroup,
                                          props.exerciseGroup,
                                          exercise,
                                      );
                                  },
                              }
                            : null,
                        props.isTemplate == false && allSetsSkipped == false
                            ? {
                                  title: "Skip all",
                                  icon: "0879-forward-circle",
                                  onClick: skipAllSets,
                              }
                            : null,
                        {
                            title: "Delete",
                            icon: "remove",
                            onClick: () => {
                                void props.newHandlers.deleteExercise(
                                    props.section,
                                    props.exerciseGroup,
                                    exercise,
                                );
                            },
                        },
                    ]}
                />
                {props.isTemplate == false && hostedVideoAvailable != null && (
                    <button
                        type="button"
                        className="
                            rounded-sm bg-white px-2 py-1 text-xs font-semibold
                            text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset
                            hover:bg-gray-50
                        "
                        onClick={() => videoDialog({video_uid: hostedVideoAvailable})}
                    >
                        <Icon icon="0875-play-circle">Video</Icon>
                    </button>
                )}
                {props.isTemplate == false && exercise.type.video_url != "" && (
                    <a
                        className="
                            rounded-sm bg-white px-2 py-1 text-xs font-semibold
                            text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset
                            hover:bg-gray-50
                        "
                        target="_blank"
                        href={exercise.type.video_url}
                        rel="noreferrer"
                    >
                        <Icon icon="0875-play-circle">Video</Icon>
                    </a>
                )}
                {props.isTemplate == false && exercise.instructions != "" && (
                    <button
                        type="button"
                        className="
                            rounded-sm bg-white px-2 py-1 text-xs font-semibold
                            text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset
                            hover:bg-gray-50
                        "
                        onClick={() => {
                            setShowInstructions(true);
                        }}
                    >
                        Instructions
                    </button>
                )}
                {props.isTemplate == false &&
                    showInstructions == true &&
                    exercise.instructions != "" && (
                        <InstructionsModal
                            name="Instructions"
                            video_url={null}
                            instructions={exercise.instructions}
                            isOpen={showInstructions}
                            onClose={() => setShowInstructions(false)}
                        />
                    )}

                {exercise.uuid == recentlyAddedExercise &&
                    props.isTemplate == false && (
                        <button
                            type="button"
                            className="
                                rounded-sm bg-white px-2 py-1 text-xs font-semibold
                                text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset
                                hover:bg-gray-50
                            "
                            onClick={() => {
                                setShowExerciseDetail(true);
                            }}
                        >
                            History
                        </button>
                    )}
                {exercise.uuid == recentlyAddedExercise &&
                    variant.kind !== "cardio" && (
                        <button
                            type="button"
                            className="
                                rounded-sm bg-white px-2 py-1 text-xs font-semibold
                                text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset
                                hover:bg-gray-50
                            "
                            onClick={() => {
                                void clearExerciseSets(exercise);
                            }}
                        >
                            Clear
                        </button>
                    )}
            </div>
            {showInstructions && props.isTemplate == true && (
                <div className="my-2">
                    <forms.PWidget
                        field={definitionForm.fields.instructions}
                        className="
                            block w-full rounded-md border-0 py-1.5 text-gray-900
                            shadow-xs ring-1 ring-gray-300 ring-inset
                            placeholder:text-gray-400
                            read-only:bg-slate-100
                            focus:ring-2 focus:ring-indigo-600 focus:ring-inset
                            sm:text-sm/6
                        "
                    />
                </div>
            )}

            {showProgression != null && props.isTemplate == true && (
                <fieldset className="my-2 space-y-3 rounded-md border border-gray-300 p-4">
                    <forms.PField field={progressionForm.fields.progression} />
                    {showProgression == "" && (
                        <forms.PField field={progressionDetailsForm.fields.name} />
                    )}
                    <forms.Label
                        error={null}
                        label={progressionDetailsForm.fields.weight.label}
                    >
                        <div className="flex flex-1 rounded-md shadow-xs">
                            <widgets.WeightInput
                                key={exercise.progression?.uuid}
                                isImperial={isImperial}
                                name={progressionDetailsForm.fields.weight.name}
                                value={progressionDetailsForm.fields.weight.value}
                                onChange={(value) =>
                                    progressionDetailsForm.fields.weight.onChange(value)
                                }
                                className={forms.getFieldClass({
                                    readOnly: props.readOnly,
                                })}
                                readOnly={props.readOnly}
                            />
                            <span
                                className={forms.getSuffixClass({
                                    readOnly: props.readOnly,
                                })}
                            >
                                {isImperial ? "lb" : "kg"}
                            </span>
                        </div>
                    </forms.Label>
                    {[].length == 1 && (
                        <forms.PField
                            field={{
                                ...progressionDetailsForm.fields.type,
                                disabledOptions: ["DOUBLE_LINEAR"],
                            }}
                        />
                    )}
                </fieldset>
            )}

            {allSetsSkipped ? (
                <div className="flex items-center gap-2 py-1 text-base text-gray-600">
                    <span className="pl-5">Exercise skipped</span>
                    <button
                        type="button"
                        onClick={unskipAllSets}
                        disabled={props.readOnly}
                        className="
                            rounded-sm bg-white px-2 py-1 text-xs font-semibold
                            text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset
                            hover:bg-gray-50
                            disabled:opacity-50
                        "
                    >
                        Restore
                    </button>
                </div>
            ) : (
                <>
                    {variant.kind === "cardio" ? (
                        <Cardio
                            exercise={variant.exercise}
                            readOnly={props.readOnly}
                            isTemplate={props.isTemplate}
                            onSetsRemoved={(uuids) => {
                                if (props.isTemplate == false) {
                                    for (const exerciseSetUUID of uuids) {
                                        void processResponse(
                                            server.journal.workouts.delete_exercise_set(
                                                {
                                                    exercise_set_uuid: exerciseSetUUID,
                                                },
                                            ),
                                            {quiet: true},
                                        );
                                    }
                                }
                            }}
                        />
                    ) : (
                        <StrengthSets
                            exercise={variant.exercise}
                            readOnly={props.readOnly}
                            isTemplate={props.isTemplate}
                            onDragStart={onDragStart}
                            onDragEnd={onDragEnd}
                            deleteExerciseSet={deleteExerciseSet}
                        />
                    )}
                    {variant.kind === "strength" && (
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => addSet(exercise)}
                                disabled={props.readOnly}
                                className={classNames(
                                    exercise.sets.length > 0 && "ml-[56px]",
                                    "rounded-sm bg-indigo-600 disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none px-2 py-1 text-xs font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600",
                                    "h-[25px]! text-center",
                                    props.readOnly &&
                                        "disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none",
                                )}
                            >
                                Add set
                            </button>
                            {(workoutContext?.scope == "client" ||
                                workoutContext?.scope == "manager" ||
                                profile?.user.casted_dismissed_announcements.includes(
                                    "HAS_VIDEO_ACCESS",
                                ) === true) &&
                                props.isTemplate == false &&
                                props.readOnly == false && (
                                    <>
                                        {clientVideoUID != null ? (
                                            <typography.DropdownButton
                                                onClick={() => {
                                                    if (
                                                        uploadingVideo == "done" &&
                                                        realm == "app"
                                                    ) {
                                                        dispatch(
                                                            notify(
                                                                "Your video was uploaded and is processing.",
                                                                {type: "info"},
                                                            ),
                                                        );
                                                    } else {
                                                        void videoDialog({
                                                            video_uid: clientVideoUID,
                                                        });
                                                    }
                                                }}
                                                items={[
                                                    {
                                                        icon: "repeat",
                                                        title: "Replace",
                                                        onClick: () => {
                                                            fileInputRef.current?.click();
                                                        },
                                                    },
                                                    {
                                                        icon: "remove",
                                                        title: "Delete",
                                                        onClick: () => {
                                                            props.exercise.__update({
                                                                video_uid: null,
                                                            });
                                                        },
                                                    },
                                                ]}
                                            >
                                                <div
                                                    className={classNames(
                                                        "relative pl-4",
                                                        uploadingVideo == "uploading" &&
                                                            "invisible",
                                                    )}
                                                >
                                                    <typography.SimpleIcon
                                                        icon="0875-play-circle"
                                                        className="
                                                            absolute top-0.5 left-0
                                                        "
                                                    />
                                                    Your video
                                                </div>
                                                {uploadingVideo == "uploading" && (
                                                    <div
                                                        className={classNames(
                                                            "absolute inset-0 flex justify-center items-center",
                                                        )}
                                                    >
                                                        <Icon
                                                            large
                                                            pulse
                                                            icon="spinner"
                                                        />
                                                    </div>
                                                )}
                                            </typography.DropdownButton>
                                        ) : (
                                            <button
                                                onClick={() =>
                                                    fileInputRef.current?.click()
                                                }
                                                className={classNames(
                                                    "relative cursor-pointer inline-block rounded-sm bg-indigo-600 disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none px-2 py-1 text-xs font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600",
                                                    "h-[25px]! text-center",
                                                    //props.readOnly &&
                                                    //    "disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none",
                                                )}
                                            >
                                                <div
                                                    className={classNames(
                                                        uploadingVideo && "invisible",
                                                    )}
                                                >
                                                    Upload video
                                                </div>
                                                {uploadingVideo == "uploading" && (
                                                    <div
                                                        className={classNames(
                                                            "absolute inset-0 flex justify-center items-center",
                                                        )}
                                                    >
                                                        <Icon
                                                            large
                                                            pulse
                                                            icon="spinner"
                                                        />
                                                    </div>
                                                )}
                                            </button>
                                        )}
                                    </>
                                )}
                            {props.isTemplate == false &&
                                props.readOnly == true &&
                                clientVideoUID != null &&
                                (workoutContext?.scope == "client" ||
                                    workoutContext?.scope == "manager" ||
                                    profile?.user.casted_dismissed_announcements.includes(
                                        "HAS_VIDEO_ACCESS",
                                    ) === true) && (
                                    <button
                                        onClick={() =>
                                            void videoDialog({
                                                video_uid: clientVideoUID,
                                            })
                                        }
                                        className={classNames(
                                            "relative cursor-pointer inline-block rounded-sm bg-indigo-600 disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none px-2 py-1 text-xs font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600",
                                            "h-[25px]! text-center",
                                            // props.readOnly &&
                                            //     "disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none",
                                        )}
                                    >
                                        <div
                                            className={classNames(
                                                "relative pl-4",
                                                uploadingVideo == "uploading" &&
                                                    "invisible",
                                            )}
                                        >
                                            <typography.SimpleIcon
                                                icon="0875-play-circle"
                                                className="absolute top-0.5 left-0"
                                            />
                                            Client video
                                        </div>
                                    </button>
                                )}
                            <select
                                value={exercise.rest_duration?.toString() ?? ""}
                                disabled={props.readOnly}
                                onChange={(event) => {
                                    const value =
                                        event.target.value === ""
                                            ? null
                                            : parseInt(event.target.value);
                                    exercise.__update({
                                        rest_duration: value,
                                    });
                                }}
                                className={classNames(
                                    "rounded-sm bg-indigo-600 disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none px-2 py-1 text-xs font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600",
                                    "h-[25px]! text-center appearance-none! border-none!",
                                    props.readOnly &&
                                        "disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none disabled:opacity-100",
                                )}
                                style={{
                                    backgroundImage: "none",
                                    // safari
                                    textAlignLast: "center",
                                }}
                            >
                                <option value="">
                                    {exercise.rest_duration != null
                                        ? "Remove"
                                        : "Rest timer"}
                                </option>
                                <option value="15">15s rest</option>
                                <option value="30">30s rest</option>
                                <option value="45">45s rest</option>
                                <option value="60">1m rest</option>
                                <option value="90">1:30m rest</option>
                                <option value="120">2m rest</option>
                                <option value="180">3m rest</option>
                                <option value="240">4m rest</option>
                                <option value="300">5m rest</option>
                            </select>
                            <input
                                ref={fileInputRef}
                                type="file"
                                className="hidden"
                                accept="video/*"
                                onChange={async (event) => {
                                    const file = event.target.files?.[0] ?? null;

                                    if (file != null) {
                                        setUploadingVideo("uploading");

                                        try {
                                            const video_uid = await uploadVideo({
                                                file,
                                                onProgress: () => {},
                                                user_id: profile!.user.id,
                                            });
                                            exercise.__update({video_uid});
                                            void analytics.track(
                                                "CLIENT_UPLOADED_VIDEO",
                                            );
                                        } catch (error: unknown) {
                                            Sentry.captureException(error);
                                        } finally {
                                            setUploadingVideo("done");
                                        }
                                    }
                                }}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export const ExerciseGroup = (props: {
    newHandlers: WorkoutHandlers;
    isFirst: boolean;
    previousGroup: workouts.ExerciseGroup | null;
    exerciseGroup: workouts.ExerciseGroup;
    section: workouts.WorkoutSection;
    workout_uuid: UUID;
    draggable: DraggableProvided;
    isDragging: boolean;
    readOnly: boolean;
    isTemplate: boolean;
    handlers: {
        addSet: (exercise: workouts.Exercise) => void;
        addToSuperSet: (
            previousGroup: workouts.ExerciseGroup | null,
            currentGroup: workouts.ExerciseGroup,
            exercise: workouts.Exercise,
        ) => void;
        removeFromSuperSet: (
            group: workouts.ExerciseGroup,
            exercise: workouts.Exercise,
        ) => void;
        deleteExerciseSet: (
            exercise: workouts.Exercise,
            exerciseSetUUID: string,
        ) => void;
        setExerciseSets: (
            exerciseHandler: workouts.Exercise,
            nextExercise: server.journal.workouts.ExerciseHistory.output,
        ) => void;
        recentlyAddedExercise:
            | `${string}-${string}-${string}-${string}-${string}`
            | null;
        clearExerciseSets: (exercise: workouts.Exercise) => void;
        onDragStart: () => void;
        onDragEnd: (result: DropResult) => void;
        isTemplate: boolean;
    };
}) => {
    if (props.exerciseGroup.exercises.length == 0) {
        return (
            <div
                ref={props.draggable.innerRef}
                {...props.draggable.draggableProps}
                {...props.draggable.dragHandleProps}
                className=""
            >
                {props.exerciseGroup.uuid}
            </div>
        );
    }
    /*
    const exerciseGroup =
        props.exerciseGroup.exercises.length > 1
            ? {type: "group" as const, group: props.exerciseGroup.exercises}
            : {type: "exercise" as const, reference: props.exerciseGroup.exercises[0]};
    */
    const exercises = props.exerciseGroup.exercises;
    const isGroup = exercises.length > 1;

    // indigo-50
    const backgroundColor = props.isDragging
        ? "#eef2ff "
        : "oklch(97.5% 0.001 106.423)";

    const styles =
        isGroup == true
            ? {
                  background: `
                linear-gradient(${backgroundColor},${backgroundColor}) padding-box,
                linear-gradient(90deg,#374FDE 30px,transparent 0) border-box
                `,
                  // backgroundRepeat: "no-repeat",
                  border: "2px solid transparent",
              }
            : undefined;

    return (
        <div
            ref={props.draggable.innerRef}
            {...props.draggable.draggableProps}
            style={{...props.draggable.draggableProps.style, ...styles}}
            className={classNames(
                "py-2 flex flex-col gap-4 rounded-md",
                isGroup == true && "pl-[8px]! pt-1! pb-3! my-2 relative",
                props.isDragging && "bg-indigo-50",
            )}
        >
            {isGroup == true && (
                <h5 className="absolute top-[-9px] left-[36px] text-xs">Superset</h5>
            )}
            {exercises.map((exercise, exerciseIndex) => (
                <Exercise
                    key={exercise.uuid}
                    {...props}
                    groupDraggable={
                        isGroup == false || exerciseIndex == 0 ? props.draggable : null
                    }
                    exercise={exercise}
                />
            ))}
        </div>
    );
};

function WorkoutSection(props: {
    workout_uuid: UUID;
    isTemplate: boolean;
    readOnly?: boolean;
    section: workouts.WorkoutSection;
    addExercise: React.ReactNode;
    deleteSection: React.ReactNode;
    children: React.ReactNode;
}) {
    const {section} = props;

    const sectionForm = forms.usePform(
        {
            type: {
                type: "select",
                options: [
                    ["BASIC", "Basic"],
                    ["ROUNDS", "Rounds"],
                    ["DURATION", "Duration"],
                ],
                required: true,
            },
            name: {
                type: "text",
            },
            instructions: {
                type: "textarea",
                placeholder: "Instructions for this section...",
            },
            rounds: {
                type: "number",
                required: false,
            },
            duration: {
                type: "duration",
                required: false,
            },
        },
        // TODO: figure this out.
        {
            ...section,
            name: section.name ?? "",
        },
        section.__update,
    );

    const children = (
        <WorkoutSectionContext.Provider value={section}>
            {props.children}
        </WorkoutSectionContext.Provider>
    );
    if (section.type == "BASIC" && (section.name == "" || section.name == null)) {
        return <>{children}</>;
    }

    return (
        <div
            key={section.uuid}
            className="
                -mx-1 space-y-2 rounded-md border-2 border-dashed border-indigo-400 p-2
                sm:p-4
            "
        >
            {section.name != null && (
                <>
                    <div className="flex items-center gap-2">
                        <h2
                            className="
                                flex-1 text-lg font-semibold
                                sm:text-xl
                            "
                        >
                            {section.name}
                        </h2>
                        {section.type != "BASIC" && (
                            <div>
                                For{" "}
                                {server.core.models.WorkoutSection.Type[section.type]}
                            </div>
                        )}
                    </div>
                    {(sectionForm.values.instructions != "" || props.isTemplate) && (
                        <forms.PWidget
                            field={sectionForm.fields.instructions}
                            className={classNames(
                                forms.getFieldClass({
                                    readOnly:
                                        props.readOnly == true ||
                                        props.isTemplate == false,
                                }),
                                "min-h-[100px] sm:min-h[150px] block w-full rounded-md!",
                            )}
                        />
                    )}
                </>
            )}
            {children}
            {(sectionForm.values.type == "ROUNDS" ||
                sectionForm.values.type == "DURATION") && (
                <div className="flex gap-2">
                    <forms.PField
                        field={{
                            ...sectionForm.fields.duration,
                            readOnly: props.isTemplate && section.type == "ROUNDS",
                        }}
                    />
                    <forms.PField
                        field={{
                            ...sectionForm.fields.rounds,
                            readOnly: props.isTemplate && section.type == "DURATION",
                        }}
                    />
                </div>
            )}
            <div className="flex gap-2">
                {props.addExercise}
                {props.deleteSection}
            </div>
        </div>
    );
}

function useWorkoutHandlers(props: {isTemplate: boolean}) {
    const deleteExercise = (
        section: workouts.WorkoutSection,
        group: workouts.ExerciseGroup,
        exercise: workouts.Exercise,
    ) => {
        void analytics.track("DELETED_EXERCISE", {exercise_name: exercise.type.name});

        if (group.exercises.length == 1) {
            if (props.isTemplate == false) {
                void processResponse(
                    server.journal.workouts.delete_exercise_group({
                        source_exercise_group: group.uuid,
                    }),
                    {quiet: true},
                );
            }

            group.__delete();
        } else {
            if (props.isTemplate == false) {
                void processResponse(
                    server.journal.workouts.delete_exercise({
                        exercise_uuid: exercise.uuid,
                    }),
                    {quiet: true},
                );
            }
            exercise.__delete();
        }
    };

    return {
        deleteExercise,
    };
}

type WorkoutHandlers = ReturnType<typeof useWorkoutHandlers>;

interface WorkoutProps {
    readOnly?: true;
    workout_uuid: UUID;
    handlers: ReturnType<typeof workouts.useWorkout>;
    isTemplate: boolean;
}

export const Workout = (props: WorkoutProps) => {
    const handlers = useWorkoutHandlers(props);
    const exerciseDialog = journeys.useExercisesDialog();

    React.useEffect(() => {
        const onFocus = () => {};

        window.addEventListener("focus", onFocus);

        return () => {
            window.removeEventListener("focus", onFocus);
        };
    }, []);

    const [recentlyAddedExercise, setRecentlyAddedExercise] =
        React.useState<UUID | null>(null);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setRecentlyAddedExercise(null);
        }, 8000);
        return () => clearTimeout(timer);
    }, [recentlyAddedExercise]);
    const isImperial = forms.useIsImperial();

    const getDefaultDistanceType = (
        category: server.journal.workouts.ExerciseType.output["category"],
    ) => {
        switch (category) {
            case "REPS_DISTANCE":
            case "WEIGHT_DISTANCE":
            case "WEIGHT_DURATION_DISTANCE":
            case "DURATION_DISTANCE":
                return isImperial ? "FEET" : "METERS";
            case "CARDIO":
                return isImperial ? "MILES" : "KILOMETERS";
            case "WEIGHT":
            case "BODYWEIGHT":
            case "DURATION":
            case "MEDLEY":
                return null;
        }
        const _exhaustiveCheck: never = category;
        return _exhaustiveCheck;
    };

    const getDefaultWeightType = (
        category: server.journal.workouts.ExerciseType.output["category"],
    ) => {
        switch (category) {
            case "WEIGHT":
            case "BODYWEIGHT":
            case "WEIGHT_DISTANCE":
            case "WEIGHT_DURATION_DISTANCE":
                return "VALUE";
            case "DURATION_DISTANCE":
            case "CARDIO":
            case "REPS_DISTANCE":
            case "DURATION":
            case "MEDLEY":
                return null;
        }
        const _exhaustiveCheck: never = category;
        return _exhaustiveCheck;
    };

    const addSet = (exercise: workouts.Exercise) => {
        const previousSet = [exercise.sets[exercise.sets.length - 1], null][0];

        exercise.__update({
            sets: [
                ...exercise.sets,
                {
                    ...previousSet,

                    uuid: crypto.randomUUID(),
                    type: previousSet?.type ?? "WORKING",
                    instructions: previousSet?.instructions ?? "",
                    weight_type: previousSet?.weight_type ?? null,
                    weight_percentage: previousSet?.weight_percentage ?? null,
                    progression: null,
                    distance_type:
                        previousSet?.distance_type ??
                        getDefaultDistanceType(exercise.type.category),
                    duration_type:
                        previousSet?.duration_type ??
                        (exercise.type.category == "DURATION" ? "SECONDS" : null),
                    speed_type: previousSet?.speed_type ?? null,
                    duration: previousSet?.duration ?? null,
                    distance: previousSet?.distance ?? null,
                    speed: previousSet?.speed ?? null,
                    incline: previousSet?.incline ?? null,
                    resistance: previousSet?.resistance ?? null,
                    reps: previousSet?.reps ?? null,
                    rpe: previousSet?.rpe ?? null,
                    weight: previousSet?.weight ?? null,
                    status: "INCOMPLETE",
                    attributes: previousSet?.attributes ?? [],
                },
            ],
        });
    };

    const deleteExerciseSet = (
        exercise: workouts.Exercise,
        exerciseSetUUID: string,
    ) => {
        exercise.__update({
            sets: exercise.sets.filter((set) => set.uuid != exerciseSetUUID),
        });

        void analytics.track("DELETED_EXERCISE_SET");

        if (props.isTemplate == false) {
            void processResponse(
                server.journal.workouts.delete_exercise_set({
                    exercise_set_uuid: exerciseSetUUID,
                }),
                {quiet: true},
            );
        }
    };

    const setExerciseSets = (
        exerciseHandler: workouts.Exercise,
        nextExercise: server.journal.workouts.ExerciseHistory.output,
    ) => {
        void analytics.track("USED_EXERCISE_SET_HISTORY", {
            exercise_name: exerciseHandler.type.name,
        });
        exerciseHandler.__update({
            rest_duration: nextExercise.rest_duration,
            instructions: nextExercise.instructions,
            sets: nextExercise.sets.map((set) => ({
                ...set,
                uuid: crypto.randomUUID(),
                status: "INCOMPLETE",
            })),
        });

        if (props.isTemplate == false) {
            void processResponse(
                server.journal.workouts.clear_exercise_sets({
                    exercise_uuid: exerciseHandler.uuid,
                }),
                {quiet: true},
            );
        }
    };

    const clearExerciseSets = (exercise: workouts.Exercise) => {
        void analytics.track("CLEARED_EXERCISE_SETS", {
            exercise_name: exercise.type.name,
        });

        exercise.__update({
            sets: [
                {
                    uuid: crypto.randomUUID(),
                    type: "WORKING",
                    instructions: "",
                    distance_type: getDefaultDistanceType(exercise.type.category),
                    duration_type:
                        exercise.type.category == "DURATION" ? "SECONDS" : null,
                    speed_type: null,
                    duration: null,
                    distance: null,
                    speed: null,
                    incline: null,
                    resistance: null,
                    reps: null,
                    progression: null,
                    rpe: null,
                    weight: null,
                    weight_type: getDefaultWeightType(exercise.type.category),
                    weight_percentage: null,
                    status: "INCOMPLETE",
                    attributes: [],
                },
            ],
        });

        if (props.isTemplate == false) {
            void processResponse(
                server.journal.workouts.clear_exercise_sets({
                    exercise_uuid: exercise.uuid,
                }),
                {quiet: true},
            );
        }

        setRecentlyAddedExercise(null);
    };

    const createExercise = (
        targetSection: workouts.WorkoutSection | null,
        exerciseType: server.journal.workouts.ExerciseType.output,
    ) => {
        const latestInstance = [exerciseType.latest_instances[0], null][0];

        const uuid = crypto.randomUUID();

        props.handlers.setSections((sections) =>
            produce(sections, (draftState) => {
                let section: server.journal.workouts.WorkoutSection.output | null =
                    null;

                if (targetSection == null) {
                    section = [draftState[draftState.length - 1], null][0];

                    if (section == null || section.name != null) {
                        draftState.push({
                            uuid: crypto.randomUUID(),
                            name: null,
                            copied_from: null,
                            instructions: "",
                            type: "BASIC",
                            rounds: null,
                            duration: null,
                            groups: [],
                        });
                        section = draftState[draftState.length - 1];
                    }
                } else {
                    section = draftState.find((s) => s.uuid == targetSection.uuid)!;
                }

                const toCreate: server.journal.workouts.Exercise.output = {
                    // TODO this goes away
                    uuid,
                    type: exerciseType,
                    mode: "NORMAL",
                    structure: "STRAIGHT",
                    instructions: "",
                    progression: null,
                    video_uid: null,
                    sets: [],
                    rest_duration: null,
                };

                if (latestInstance != null && latestInstance.sets.length > 0) {
                    setRecentlyAddedExercise(uuid);

                    section.groups.push({
                        uuid: crypto.randomUUID(),
                        exercises: [
                            {
                                ...toCreate,
                                rest_duration: latestInstance.rest_duration,
                                sets: latestInstance.sets.map((set) => ({
                                    ...set,
                                    uuid: crypto.randomUUID(),
                                    status: "INCOMPLETE",
                                })),
                            },
                        ],
                    });
                } else {
                    section.groups.push({
                        uuid: crypto.randomUUID(),
                        exercises: [
                            {
                                ...toCreate,
                                sets: [
                                    {
                                        uuid: crypto.randomUUID(),
                                        type: "WORKING",
                                        instructions:
                                            exerciseType.category == "MEDLEY"
                                                ? exerciseType.description
                                                : "",
                                        distance_type: getDefaultDistanceType(
                                            exerciseType.category,
                                        ),
                                        duration_type:
                                            exerciseType.category == "DURATION"
                                                ? "SECONDS"
                                                : null,
                                        speed_type: null,
                                        duration: null,
                                        progression: null,
                                        distance: null,
                                        speed: null,
                                        incline: null,
                                        resistance: null,
                                        reps: null,
                                        rpe: null,
                                        weight: null,
                                        status: "INCOMPLETE",
                                        attributes: [],

                                        weight_type: getDefaultWeightType(
                                            exerciseType.category,
                                        ),
                                        weight_percentage: null,
                                    },
                                ],
                            },
                        ],
                    });
                }
            }),
        );

        void analytics.track("CREATED_EXERCISE", {
            exercise_name: exerciseType.name,
        });
    };

    const onDragStart = () => {
        void hardware.Haptics.impact({
            style: hardware.ImpactStyle.Heavy,
        });
    };

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const source = result.source;
        const destination = result.destination;

        if (
            source.droppableId === destination.droppableId &&
            source.index === destination.index
        ) {
            return;
        }

        if (result.type == "EXERCISE_GROUP") {
            props.handlers.setSections((prevState) =>
                produce(prevState, (draftState) => {
                    const sourceSection = draftState.find(
                        (section) => section.uuid == source.droppableId,
                    )!;
                    const destinationSection = draftState.find(
                        (section) => section.uuid == destination.droppableId,
                    )!;

                    const group = sourceSection.groups[source.index];
                    sourceSection.groups.splice(source.index, 1);
                    destinationSection.groups.splice(destination.index, 0, group);
                }),
            );
        }

        if (result.type == "EXERCISE_SET") {
            void analytics.track("SORTED_EXERCISE_SETS");

            const sourceExercise = props.handlers.sections
                .map((s) => s.groups)
                .flat()
                .map((g) => g.exercises)
                .flat()
                .find((exercise) => exercise.uuid == source.droppableId)!;

            sourceExercise.__update({
                sets: produce(sourceExercise.sets, (draftState) => {
                    const sourceExerciseSet = draftState[source.index];
                    draftState.splice(source.index, 1);
                    draftState.splice(destination.index, 0, sourceExerciseSet);
                }),
            });
        }
    };

    const addToSuperSet = (
        previousGroup: workouts.ExerciseGroup | null,
        currentGroup: workouts.ExerciseGroup,
        exercise: workouts.Exercise,
    ) => {
        if (previousGroup == null) {
            return;
        }

        previousGroup.__update({
            exercises: [...previousGroup.exercises, exercise],
        });

        currentGroup.__update({
            exercises: currentGroup.exercises.filter((e) => e.uuid != exercise.uuid),
        });

        if (currentGroup.exercises.length == 1) {
            if (props.isTemplate == false) {
                void server.journal.workouts.delete_exercise_group({
                    source_exercise_group: currentGroup.uuid,
                });
            }

            props.handlers.setSections((sections) =>
                produce(sections, (draft) => {
                    const section = draft.find((s) =>
                        s.groups.some((g) => g.uuid == currentGroup.uuid),
                    )!;
                    section.groups = section.groups.filter(
                        (g) => g.uuid != currentGroup.uuid,
                    );
                }),
            );
        }
    };

    const removeFromSuperSet = (
        group: workouts.ExerciseGroup,
        exercise: workouts.Exercise,
    ) => {
        props.handlers.setSections((sections) =>
            produce(sections, (draft) => {
                const section = draft.find((s) =>
                    s.groups.some((g) => g.uuid == group.uuid),
                )!;
                const currentGroupIndex = section.groups.findIndex(
                    (g) => g.uuid == group.uuid,
                );
                const currentGroup = section.groups[currentGroupIndex];
                currentGroup.exercises = currentGroup.exercises.filter(
                    (e) => e.uuid != exercise.uuid,
                );
                section.groups.splice(currentGroupIndex + 1, 0, {
                    uuid: crypto.randomUUID(),
                    exercises: [exercise],
                });
            }),
        );
    };

    const renderedExercises =
        (section: workouts.WorkoutSection) => (provided: DroppableProvided) => (
            <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="flex flex-col"
                onChange={() => {}}
            >
                {section.groups.map((exerciseGroup, exerciseGroupIndex) => (
                    <Draggable
                        isDragDisabled={props.readOnly}
                        key={exerciseGroup.uuid}
                        draggableId={exerciseGroup.uuid}
                        index={exerciseGroupIndex}
                    >
                        {(innerProvided, snapshot) => (
                            <ExerciseGroup
                                isFirst={exerciseGroupIndex == 0}
                                section={section}
                                newHandlers={handlers}
                                workout_uuid={props.workout_uuid}
                                handlers={{
                                    addSet,
                                    addToSuperSet,
                                    removeFromSuperSet,
                                    deleteExerciseSet,
                                    recentlyAddedExercise,
                                    setExerciseSets,
                                    clearExerciseSets,
                                    onDragStart,
                                    onDragEnd,
                                    isTemplate: props.isTemplate,
                                }}
                                draggable={innerProvided}
                                previousGroup={section.groups[exerciseGroupIndex - 1]}
                                exerciseGroup={exerciseGroup}
                                isDragging={snapshot.isDragging}
                                isTemplate={props.isTemplate}
                                readOnly={props.readOnly ?? false}
                            />
                        )}
                    </Draggable>
                ))}
                {provided.placeholder}
            </div>
        );

    const renderedAddExercise = (section: workouts.WorkoutSection | null) => (
        <>
            {exerciseDialog.dialog}
            <button
                type="button"
                disabled={props.readOnly}
                onClick={async () => {
                    const result = await exerciseDialog({
                        exerciseTypesForPrediction: props.handlers.exerciseTypes.map(
                            (e) => e.uuid,
                        ),
                    });

                    if (result != null) {
                        createExercise(section, result[0]);
                    }
                }}
                className={classNames(
                    "disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none font-semibold truncate rounded-sm bg-white px-4 py-1 text-sm text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50",
                )}
            >
                Add exercise
            </button>
        </>
    );
    const sectionDialog = dialogs.useDialog({
        modal: dialogs.HModal,
        // isOpen: true,
        Content: ({close}) => {
            return (
                <SectionDialog
                    workout_uuid={props.workout_uuid}
                    close={close}
                    setSections={props.handlers.setSections}
                />
            );
        },
    });

    const renderedAddSection = props.isTemplate && (
        <button
            type="button"
            disabled={props.readOnly}
            onClick={async () => {
                await sectionDialog();
            }}
            className={classNames(
                "disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none font-semibold truncate rounded-sm bg-white px-4 py-1 text-sm text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50",
            )}
        >
            Add section
        </button>
    );

    const renderedDeleteSection = (section: workouts.WorkoutSection) =>
        props.isTemplate && (
            <button
                type="button"
                disabled={props.readOnly}
                onClick={() => {
                    props.handlers.setSections((sections) =>
                        sections.filter((s) => s.uuid != section.uuid),
                    );
                }}
                className={classNames(
                    "disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none font-semibold truncate rounded-sm bg-white px-4 py-1 text-sm text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50",
                )}
            >
                Delete section
            </button>
        );

    return (
        <div>
            {sectionDialog.dialog}
            <div className="space-y-4">
                <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
                    {props.handlers.sections.map((section) => (
                        <WorkoutSection
                            key={section.uuid}
                            workout_uuid={props.workout_uuid}
                            section={section}
                            isTemplate={props.isTemplate}
                            addExercise={renderedAddExercise(section)}
                            deleteSection={renderedDeleteSection(section)}
                        >
                            <>
                                <Droppable
                                    droppableId={section.uuid}
                                    type="EXERCISE_GROUP"
                                >
                                    {renderedExercises(section)}
                                </Droppable>
                            </>
                        </WorkoutSection>
                    ))}
                </DragDropContext>
            </div>
            <div className="mt-2 flex gap-2">
                {renderedAddExercise(null)}
                {renderedAddSection}
            </div>
        </div>
    );
};

interface WorkoutActionsProps {
    actions: ActionsDefinition;
}

export const WorkoutActions = (props: WorkoutActionsProps) => {
    const [ready, setReady] = React.useState(false);

    React.useEffect(() => {
        setReady(true);
    }, []);

    if (ready == false) {
        return null;
    }

    const element = document.getElementById("more");

    if (element == null) {
        return null;
    }

    return createPortal(<Options items={props.actions}></Options>, element);
};
